import { Injectable } from '@angular/core';
import { LcsApiService, LoadingState } from '@lcs/shared/api';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store, select } from '@ngrx/store';
import { Observable, OperatorFunction, isObservable, merge, of, partition } from 'rxjs';
import { catchError, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { FilterExpression, serializeFilterExpression } from 'src/app/filters/filter-definition';
import { EagerSelectActions, LoadAction, LoadActionCreator, LoadFromCacheActionCreator } from './eager-select-actions';
import { EagerSelectState } from './eager-select-reducer';

/**
 * Custom RxJS operator to map an observable of 'load' actions to an observable of 'load from cache' actions
 * @param actionCreator The 'load from cache' action creator
 */
export function mapToLoadFromCache<TType, TState extends EagerSelectState<TType>>(
   actionCreator: LoadFromCacheActionCreator<TType>
): OperatorFunction<[LoadAction, TState], ReturnType<LoadFromCacheActionCreator<TType>>> {
   return map(([_action, state]: [LoadAction, TState]) => {
      return actionCreator({
         // TODO: reuse the existing selectors to take advantage of memoization
         items: (state.ids as any[]).map((id: any) => {
            return (state.entities as any)[id];
         }),
         totalCount: state.ids.length,
      });
   });
}

/**
 * Provides helper functions for creating effects for eager-loaded selects
 */
@Injectable({ providedIn: 'root' })
export class EagerSelectEffects<TStore> {
   constructor(private store: Store<TStore>, private actions$: Actions, private apiService: LcsApiService) {}

   createEagerSelectorLoadEffect<TType, TState extends EagerSelectState<TType>, TApiResultType = TType>(
      selector: (store: TStore) => TState,
      defaultUrl: string,
      defaultFields: string[],
      defaultOrderingOptions: string[],
      actions: EagerSelectActions<TType, TApiResultType>,
      staticFilters?: string[] | Observable<string[]> | FilterExpression | Observable<FilterExpression>,
      staticUseIntermediateEmptyState?: boolean | Observable<boolean>
   ) {
      return createEffect(() => {
         const [fromApi, fromCache] = this.partition(this.store, this.actions$, selector, actions.load, defaultUrl);
         const filters$ = isObservable(staticFilters) ? staticFilters : of(staticFilters);
         const useIntermediateEmptyState$ = isObservable(staticUseIntermediateEmptyState)
            ? staticUseIntermediateEmptyState
            : of(staticUseIntermediateEmptyState);
         return merge(
            fromCache.pipe(mapToLoadFromCache(actions.loadFromCache)),
            fromApi.pipe(
               withLatestFrom(filters$, useIntermediateEmptyState$),
               switchMap(([[action, _state], filters, useIntermediateEmptyState]) => {
                  let fields = defaultFields;
                  if (action.additionalFields) {
                     fields = fields.concat(action.additionalFields);
                  }
                  if (action.filterOverrides) {
                     filters = action.filterOverrides;
                  }
                  if (action.useIntermediateEmptyState !== undefined) {
                     useIntermediateEmptyState = action.useIntermediateEmptyState;
                  }

                  const url = action.urlOverride || defaultUrl;
                  const filtersArray = Array.isArray(filters)
                     ? [filters.join(' AND ')]
                     : filters
                     ? [serializeFilterExpression(filters ?? null)]
                     : [];

                  const apiCall = this.apiService.getCollectionResponse<TApiResultType[]>(
                     url,
                     fields,
                     filtersArray,
                     defaultOrderingOptions
                  );
                  const apiCallPipe = apiCall.pipe(
                     map((response) => {
                        return actions.loadFromApiSuccess({
                           fields,
                           filters: filtersArray,
                           items: response.body || [],
                           totalCount: this.apiService.getTotalCount(response),
                           url,
                        });
                     }),
                     catchError((error) => {
                        return of(actions.loadFromApiFailure({ error }));
                     })
                  );

                  // Not merging in loadFromApi action when custom filters are present
                  // to prevent unnecessary empty state update before request finishes
                  return useIntermediateEmptyState
                     ? merge(of(actions.loadFromApi({ fields, url })), apiCallPipe)
                     : apiCallPipe;
               })
            )
         );
      });
   }

   /**
    * Partitions a stream of load actions into two streams:
    * a stream of actions that should trigger an load from the API, and
    * a stream of actions that should trigger a load from the cache.
    * @param selector The eager select feature selector
    * @param loadAction The eager select load action creator
    */
   partition<TType, TState extends EagerSelectState<TType>>(
      store: Store<TStore>,
      actions: Actions,
      selector: (store: TStore) => TState,
      loadAction: LoadActionCreator,
      defaultUrl: string
   ) {
      return partition(
         actions.pipe(
            ofType(loadAction),
            switchMap((action) => {
               return of(action).pipe(withLatestFrom(store.pipe(select(selector))));
            })
         ),
         ([action, state]) => {
            return (
               state.callState !== LoadingState.Loaded ||
               this.hasNewFields(state.fields, action.additionalFields) ||
               this.hasNewUrl(state.url, action.urlOverride, defaultUrl) ||
               this.hasNewFilters(state.filters, action.filterOverrides)
            );
         }
      );
   }

   private hasNewFields(currentFields: string[], newFields: string[] | null | undefined): newFields is string[] {
      if (!newFields || newFields.length === 0 || currentFields.length === 0) {
         return false;
      }
      const set = new Set<string>(currentFields);
      return newFields.some((field) => {
         return !set.has(field);
      });
   }

   private hasNewFilters(currentFilters: string[] | undefined, newFilters: string[] | undefined): boolean {
      if (!newFilters && !currentFilters) {
         return false;
      }
      if (
         (newFilters && currentFilters && currentFilters.length !== newFilters.length) ||
         (newFilters && !currentFilters) ||
         (!newFilters && currentFilters)
      ) {
         return true;
      }

      const set = new Set<string>(currentFilters);
      return !!(
         newFilters &&
         newFilters.some((filter) => {
            return !set.has(filter);
         })
      );
   }

   private hasNewUrl(previousUrl: string | null, newUrl: string | null | undefined, defaultUrl: string): boolean {
      // a falsy newUrl is considered to be equivalent to the default url
      return (newUrl || defaultUrl) !== previousUrl;
   }
}
